import type { CSSProperties } from 'react';

/**
 * The Aghosha logo — the curtain mark and the lowercase wordmark.
 *
 * The mark is six curtain folds drawn back from the centre: the stage opening
 * before an event. It is split into a left and a right half (`lm-l`, `lm-r`)
 * so the header can part the curtains on hover and the menu button can open
 * them fully. Colours come from `--logo-mark` and `--logo-word`, which the
 * stylesheet sets per theme and per surface (always gold on cream over a
 * dark photo band).
 */

/** The six folds, left three first; `i` staggers the drop-in animation. */
const LEFT = [
  { i: 0, d: 'M0 0L42.7 0C31 21.1 15.9 39.9 0 56Z' },
  { i: 1, d: 'M66.8 0L90.3 0C74.5 58.4 28.5 114.1 0 140.8L0 100.1C34.6 64.3 53.6 29.2 66.8 0Z' },
  { i: 2, d: 'M109.8 0L136.4 0C90.1 151.6 51.9 213.1 0 274.3L0 195.4C74.7 123.7 104.3 21.3 109.8 0Z' },
];
const RIGHT = [
  { i: 2, d: 'M138.5 0L165.1 0C170.6 21.3 200.2 123.7 274.9 195.4L274.9 274.3C223 213.1 184.9 151.6 138.5 0Z' },
  { i: 1, d: 'M184.6 0L208.1 0C221.3 29.2 240.3 64.3 274.9 100.1L274.9 140.8C246.4 114.1 200.4 58.4 184.6 0Z' },
  { i: 0, d: 'M232.2 0L274.9 0L274.9 56C259 39.9 243.9 21.1 232.2 0' },
];

/** a · g · h · o · s · h · a, each glyph placed by its own translate. */
const A = 'M67 2.4C84.7 2.4 98.2 -4.2 107.2 -15.3L107.9 0L136.4 0L136.4 -119L105.8 -119L105.8 -105.5C97 -115.7 84 -121.8 67 -121.8C34.9 -121.8 8.3 -94.2 8.3 -59.7C8.3 -25 34.9 2.4 67 2.4ZM72.9 -25.5C53.8 -25.5 39 -40.8 39 -59.7C39 -78.6 53.8 -93.7 72.9 -93.7C91.8 -93.7 106.7 -78.6 106.7 -59.7C106.7 -40.8 91.8 -25.5 72.9 -25.5Z';
const H = 'M15.8 0L46.3 0L46.3 -73.4C46.5 -85 56.2 -94 69.2 -94C81 -94 90.2 -85.2 90.2 -73.6L90.2 0L120.9 0L120.9 -77C120.9 -101.7 102.2 -121.3 77.4 -121.3C64.9 -121.3 54.3 -116.1 46.3 -108.1L46.3 -181.8L15.8 -181.8Z';
const GLYPHS = [
  { x: 351, d: A },
  {
    x: 503.7,
    d: 'M71.8 56.7C109 56.7 137.1 31.4 137.1 -7.1L137.1 -119L106.2 -119L106.2 -105.3C97.7 -115.2 85.2 -121.3 68.5 -121.3C34.7 -121.3 8.3 -94 8.3 -59.5C8.3 -25.2 34.5 1.2 69.2 1.2C85.5 1.2 97.7 -4.5 106.2 -14.2L106.2 -7.1C106.2 14.9 90.9 29.3 70.8 29.3C56.2 29.3 41.8 21.7 32.8 8L13.5 27.4C26.9 47 50.5 56.7 71.8 56.7ZM72.9 -25.2C53.8 -25.2 39 -40.6 39 -59.5C39 -78.6 53.8 -93.7 72.9 -93.7C91.8 -93.7 107.2 -78.6 107.2 -59.5C107.2 -40.6 91.8 -25.2 72.9 -25.2Z',
  },
  { x: 657.1, d: H },
  {
    x: 790.7,
    d: 'M71.3 2.4C106.7 2.4 134.1 -25 134.1 -59.5C134.1 -94 106.7 -121.3 71.3 -121.3C35.4 -121.3 8.3 -94 8.3 -59.5C8.3 -25 35.4 2.4 71.3 2.4ZM71.3 -26C52.4 -26 37.5 -40.8 37.5 -59.5C37.5 -78.1 52.4 -92.8 71.3 -92.8C89.5 -92.8 104.3 -78.1 104.3 -59.5C104.3 -40.8 89.5 -26 71.3 -26Z',
  },
  {
    x: 930.7,
    d: 'M53.3 2.4C79.3 2.4 98 -10.9 98 -34.9C98 -58.8 80 -66.8 62.3 -72.7C48.4 -77.2 39.9 -79.5 39.9 -87.6C39.9 -93.7 44.8 -97.7 52.4 -97.7C60.4 -97.7 69.4 -93 76.2 -86.2L94.2 -104.3C85 -114.5 70.6 -121.3 53.3 -121.3C30.5 -121.3 9.7 -109 9.7 -85C9.7 -64.2 25.2 -55.9 43.7 -49.3C57.8 -44.4 67.5 -42.2 67.5 -34C67.5 -27.6 61.8 -22.7 52.4 -22.7C43.2 -22.7 32.1 -27.9 23.8 -36.3L6.6 -15.3C16 -5.9 32.8 2.4 53.3 2.4Z',
  },
  { x: 1035.3, d: H },
  { x: 1168.9, d: A },
];

function Folds({ withClasses = true }: { withClasses?: boolean }) {
  return (
    <>
      <g className="lm-l">
        {LEFT.map((p) => (
          <path key={p.d} className={withClasses ? 'lm-s' : undefined} style={{ '--i': p.i } as CSSProperties} d={p.d} />
        ))}
      </g>
      <g className="lm-r">
        {RIGHT.map((p) => (
          <path key={p.d} className={withClasses ? 'lm-s' : undefined} style={{ '--i': p.i } as CSSProperties} d={p.d} />
        ))}
      </g>
    </>
  );
}

/** Mark + wordmark. Decorative unless given a title. */
export function Logo({ title, className = '' }: { title?: string; className?: string }) {
  return (
    <svg
      className={`logo-svg ${className}`.trim()}
      viewBox="0 0 1305.3 274.3"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <g className="lm" fill="var(--logo-mark, #d4a653)">
        <Folds />
      </g>
      <g className="lw" fill="var(--logo-word, currentColor)">
        {GLYPHS.map((g, i) => (
          <g key={g.x} transform={`translate(${g.x} 199.7)`}>
            <path className="lw-g" style={{ '--i': i } as CSSProperties} d={g.d} />
          </g>
        ))}
      </g>
    </svg>
  );
}

/** The curtain mark on its own (menu button, menu backdrop, empty states). */
export function CurtainMark({ className = 'mt-mark' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 275 275" aria-hidden="true" focusable="false">
      <Folds withClasses={false} />
    </svg>
  );
}

/** Kept for existing call sites: the full logo. */
export function BrandLockup({ size = 32 }: { onDark?: boolean; size?: number }) {
  return (
    <span className="brand-lockup" style={{ height: size }}>
      <Logo />
    </span>
  );
}
